import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';

import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import NewsCard from './components/NewsCard';
import FeaturedNewsCard from './components/FeaturedNewsCard';
import NewsCategoryFilter from './components/NewsCategoryFilter';
import pngNewsService from '../../services/pngNewsService';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';
import { complianceAlertTriggerService } from '../../services/complianceAlertTriggerService';

const PngNewsUpdates = () => {
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [featuredNews, setFeaturedNews] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [newsStats, setNewsStats] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();

    // Subscribe to real-time news updates
    const channel = pngNewsService?.subscribeToNews((newNews) => {
      setNews(prev => [newNews, ...prev]);
      loadStats();
    });

    // Subscribe to compliance alert monitoring
    const complianceChannel = complianceAlertTriggerService?.subscribeToNewsUpdates((newNews, alerts) => {
      console.log('Compliance alerts triggered for news:', newNews?.id, alerts);
    });

    return () => {
      pngNewsService?.unsubscribeFromNews(channel);
      complianceAlertTriggerService?.unsubscribeFromNewsUpdates(complianceChannel);
    };
  }, []);

  useEffect(() => {
    loadNews();
  }, [selectedCategory, searchQuery, priorityFilter]);

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadFeaturedNews(),
        loadNews(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedNews = async () => {
    try {
      const data = await pngNewsService?.getFeaturedNews(3);
      setFeaturedNews(data || []);
    } catch (error) {
      console.error('Error loading featured news:', error);
    }
  };

  const loadNews = async () => {
    try {
      const filters = {
        category: selectedCategory,
        search: searchQuery,
        priority: priorityFilter !== 'all' ? priorityFilter : null
      };
      const data = await pngNewsService?.getAllNews(filters);
      setNews(data || []);
    } catch (error) {
      console.error('Error loading news:', error);
    }
  };

  const loadStats = async () => {
    try {
      const stats = await pngNewsService?.getNewsStats();
      setNewsStats(stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e?.target?.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handlePriorityChange = (e) => {
    setPriorityFilter(e?.target?.value);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      all: 'All News',
      hr: 'HR & Employment',
      payroll: 'Payroll',
      tax: 'Tax (SWT)',
      minimum_wage: 'Minimum Wage',
      employment_act: 'Employment Act',
      gazette: 'Gazette',
      general: 'General'
    };
    return labels?.[category] || category;
  };

  const handleGlobalFilterChange = (filters) => {
    // Handle global filter changes if needed
    console.log('Global filters changed:', filters);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
      <DataRefreshIndicator />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Newspaper" size={32} color="var(--color-primary)" />
            <h1 className="text-3xl font-heading font-bold text-foreground">
              PNG Government News Updates
            </h1>
          </div>
          <p className="text-muted-foreground font-body">
            Stay informed with the latest updates from PNG Gazette and government sources on HR, Payroll, Tax, Minimum Wage, and Employment Acts.
          </p>
        </div>

        {/* Featured News Section */}
        {featuredNews?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Star" size={20} color="var(--color-warning)" />
              Featured News
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredNews?.map((item) => (
                <FeaturedNewsCard key={item?.id} news={item} />
              ))}
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search News
              </label>
              <Input
                type="text"
                placeholder="Search by title, summary, or content..."
                value={searchQuery}
                onChange={handleSearch}
                iconName="Search"
              />
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Priority
              </label>
              <Select
                value={priorityFilter}
                onChange={handlePriorityChange}
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </Select>
            </div>

            {/* Stats Summary */}
            <div className="flex items-center justify-center md:justify-end gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {newsStats?.total || 0}
                </div>
                <div className="text-xs text-muted-foreground">Total News</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-error">
                  {newsStats?.critical || 0}
                </div>
                <div className="text-xs text-muted-foreground">Critical</div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <NewsCategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          stats={newsStats}
        />

        {/* News List */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold text-foreground">
              {getCategoryLabel(selectedCategory)}
            </h2>
            <span className="text-sm text-muted-foreground">
              {news?.length} {news?.length === 1 ? 'article' : 'articles'}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
            </div>
          ) : news?.length === 0 ? (
            <EmptyStatePrompt
              icon="Newspaper"
              title="No news found"
              description="There are no news articles matching your current filters. Try adjusting your search or category selection."
              onAction={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setPriorityFilter('all');
              }}
            />
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {news?.map((item) => (
                <NewsCard key={item?.id} news={item} />
              ))}
            </div>
          )}
        </div>
      </main>

      {showRedirectModal && (
        <TeaserEditModal
          isOpen={showRedirectModal}
          onClose={closeModal}
          feature="PNG News Updates"
        />
      )}
    </div>
  );
};

export default PngNewsUpdates;